import { Component, DestroyRef, OnDestroy, OnInit, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import { SavingsPlanFacade } from '@zero/saving-plans/domain';
import { CustomerStateFacade } from '@zero/shared/util-global-state';
import { commonRoutes } from 'constants/web-app-constants';
import { combineLatestWith, filter, map } from 'rxjs';
import { SavingsPlanConfirmComponent } from '../../components/savings-plan-confirm/savings-plan-confirm.component';
import { SavingsPlanEditComponent } from '../../components/savings-plan-edit/savings-plan-edit.component';
import { CurrentStep } from '../../util/current-step';

@Component({
  templateUrl: './savings-plan-edit-page.component.html',
  imports: [SavingsPlanEditComponent, SavingsPlanConfirmComponent],
})
export class SavingsPlanEditPageComponent implements OnInit, OnDestroy {
  public readonly facade = inject(SavingsPlanFacade);
  public readonly STEPS = CurrentStep;
  public step = CurrentStep.EDIT;

  readonly #route = inject(ActivatedRoute);
  readonly #router = inject(Router);
  readonly #destroyRef = inject(DestroyRef);
  readonly #customerStateFacade = inject(CustomerStateFacade);

  public ngOnInit(): void {
    this.#customerStateFacade.loadCustomerBaseData();
    this.facade.loadSavingsPlansOverview();
    this.facade.loadOptions();
    this.facade.execDates$.pipe(takeUntilDestroyed(this.#destroyRef)).subscribe((execDates) => this.facade.changeExecDate(execDates[0]));

    this.#listenToParams();
    this.#listenToQueryParams();
    this.#listenToIntervals();
    this.#listenToSavingsPlansUpdated();
  }

  ngOnDestroy(): void {
    this.facade.clearForm();
  }

  public goBack() {
    this.facade.clearMultiSavingsPlanError();
    this.step = CurrentStep.EDIT;
  }

  public handleNoWallet() {
    this.#router.navigate([commonRoutes.postlogin.verwaltung], {
      queryParams: {
        section: 'krypto-wallet',
      },
    });
  }

  public resetAsset() {
    this.#router.navigate([commonRoutes.postlogin.add_savings_plan]);
  }

  #listenToParams = () =>
    this.#route.params
      .pipe(combineLatestWith(this.facade.savingsPlans$), takeUntilDestroyed(this.#destroyRef))
      .subscribe(([params]) => this.facade.loadExistingSavingsPlan(params['id']));

  #listenToQueryParams = () =>
    this.#route.queryParams
      .pipe(
        combineLatestWith(this.facade.dynamizationValues$, this.facade.savingsPlanToUpdate$),
        filter(([params, values]) => !!values?.length && values.every((value) => value > 0) && params['dynamization'] === 'true'),
        map(([, values]) => values[0]),
        takeUntilDestroyed(this.#destroyRef),
      )
      .subscribe((value) => this.facade.setDynamization(value));

  #listenToSavingsPlansUpdated = () =>
    this.facade.savingsPlansUpdated$
      .pipe(
        filter((updated) => updated),
        takeUntilDestroyed(this.#destroyRef),
      )
      .subscribe(() => this.#router.navigate([commonRoutes.postlogin.savings_plans]));

  #listenToIntervals = () =>
    this.facade.intervals$
      .pipe(
        combineLatestWith(this.facade.savingsPlanToUpdate$),
        filter(([intervals, savingsPlan]) => intervals?.length && !!savingsPlan),
        map(([intervals, savingsPlan]) => intervals.find((interval) => interval === savingsPlan.interval)),
        takeUntilDestroyed(this.#destroyRef),
      )
      .subscribe((interval) => this.facade.changeInterval(interval));
}
